import { useEffect, useState } from 'react';
import { Key, Loader2, Copy, Check, X } from 'lucide-react';
import { useApi } from '../lib/api';

interface Props {
  homeId: string;
  /** When true, the home is already paired — the button switches to a softer
   * "re-pair" appearance so it doesn't compete with the paired status. */
  alreadyPaired?: boolean;
}

interface PairToken {
  token: string;
  installCommand: string;
  expiresAt: number;     // unix seconds (per mint-pairing-token lambda)
}

/**
 * Mints a one-shot edge-pairing token for a home and shows it in a modal with
 * copy-to-clipboard helpers. The on-site operator runs the install command on
 * the edge box; the box claims the token via the `/pair/register` endpoint.
 *
 * The /pair/token API requires an owner (or verified super-admin) of the home and
 * does the permission check — this UI is just a thin wrapper around it. On an
 * already-paired home, a plain token only re-pairs the SAME box; replacing it with
 * a different box needs the explicit "replace" choice below (issue #430).
 */
export function PairEdgeButton({ homeId, alreadyPaired = false }: Props) {
  const api = useApi();
  const [open, setOpen] = useState(false);
  const [token, setToken] = useState<PairToken | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replaceEdge, setReplaceEdge] = useState(false);

  const onClick = async () => {
    setLoading(true);
    setError(null);
    setToken(null);
    setOpen(true);
    try {
      const t = await api.mintPairingToken(homeId, { replaceEdge: alreadyPaired && replaceEdge });
      setToken(t);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const onClose = () => {
    setOpen(false);
    setToken(null);
    setError(null);
  };

  const buttonClass = alreadyPaired
    ? 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-slate-300 bg-slate-50 text-ink-800 hover:bg-slate-100 hover:text-brand-ink text-xs disabled:opacity-40'
    : 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded border border-brand-ink bg-brand-ink text-brand-bone hover:bg-ink-800 text-xs disabled:opacity-40';

  return (
    <>
      <button onClick={onClick} disabled={loading} className={buttonClass}>
        {loading ? <Loader2 size={12} className="animate-spin" /> : <Key size={12} />}
        {loading ? 'Generating…' : alreadyPaired ? 'Re-pair edge' : 'Generate Pairing Token'}
      </button>
      {alreadyPaired && (
        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
          <input
            type="checkbox"
            checked={replaceEdge}
            onChange={(e) => setReplaceEdge(e.target.checked)}
            disabled={loading}
          />
          Replace with a different box
        </label>
      )}

      {open && (
        <PairTokenModal token={token} loading={loading} error={error} onClose={onClose} />
      )}
    </>
  );
}

function PairTokenModal({
  token,
  loading,
  error,
  onClose,
}: {
  token: PairToken | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
}) {
  // Close on Escape unless we're mid-fetch (avoid orphaning the request UX).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 backdrop-blur-sm py-10 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pair-token-modal-title"
        className="w-full max-w-2xl bg-white border border-slate-200 rounded-xl shadow-glow"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
          <h2 id="pair-token-modal-title" className="text-lg font-semibold text-brand-ink inline-flex items-center gap-2">
            <Key size={16} className="text-slate-500" /> Edge pairing token
          </h2>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="text-slate-600 hover:text-brand-ink p-1 rounded hover:bg-slate-100 disabled:opacity-40"
            aria-label="Close edge pairing dialog"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-5 space-y-4">
          {loading && (
            <div className="text-sm text-ink-800 inline-flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Minting one-shot token…
            </div>
          )}

          {error && (
            <div className="p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
              {error}
            </div>
          )}

          {token && (
            <>
              <p className="text-sm text-ink-800">
                Run this command on the edge box (the small Linux machine at the home —
                <span className="font-mono"> manor</span>, a NUC, or a MacBook with{' '}
                <span className="font-mono">@manor/edge</span> installed). The token is single-use
                and expires shortly.
              </p>

              <Section label="Install command">
                <CopyableBlock value={token.installCommand} multiline />
              </Section>

              <Section label="Token">
                <CopyableBlock value={token.token} />
              </Section>

              <div className="text-xs text-slate-600">
                Expires at:{' '}
                <span className="text-brand-ink font-mono">{formatExpiry(token.expiresAt)}</span>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-3 py-1.5 rounded text-sm text-ink-800 hover:text-brand-ink border border-slate-300 hover:border-slate-400 disabled:opacity-40"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10px] text-slate-600 uppercase tracking-[0.16em] mb-1.5">{label}</div>
      {children}
    </div>
  );
}

function CopyableBlock({ value, multiline = false }: { value: string; multiline?: boolean }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts — surface the failure quietly
      // by leaving copied=false. The user can still select the text manually.
    }
  };

  return (
    <div className="relative">
      <pre
        className={`text-[12px] font-mono bg-slate-100 rounded-lg p-3 pr-12 overflow-x-auto text-brand-ink leading-relaxed ${
          multiline ? 'whitespace-pre-wrap break-all' : 'whitespace-nowrap'
        }`}
      >
        {value}
      </pre>
      <button
        type="button"
        onClick={onCopy}
        className="absolute top-2 right-2 inline-flex items-center gap-1 text-[11px] text-ink-800 hover:text-brand-ink px-1.5 py-1 rounded border border-slate-300 hover:border-slate-400 bg-white"
        title="Copy to clipboard"
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function formatExpiry(epochSeconds: number): string {
  if (!epochSeconds || !Number.isFinite(epochSeconds)) return String(epochSeconds);
  return new Date(epochSeconds * 1000).toLocaleString();
}
